import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../../store";
import {
  createFloorInwardRequest,
  getProductRawMaterials,
  getMaterialsRawMaterials,
} from "../../../store/slices/floorInwardSlice";
import { fetchProductsList } from "../../../store/slices/productSlice";
import {
  IWarehouseRawMaterial,
  UNITS,
  UnitType,
} from "../../../models/warehouseRawMaterial.model";
import { toast } from "react-hot-toast";
import Select, { StylesConfig } from "react-select";
import {
  getUnitsByType,
  getDefaultUnitForType,
} from "../../../utils/warehouse.util";
import AsyncSelect from "react-select/async";
import { fetchRawMaterialsThunk } from "../../../store/slices/warehouseSlice";
import { fetchDropdownData } from "../../../store/slices/storeInwardSlice";
import UserSelectorSearch from "../../../components/Users/UserSelectorSearch";
import { fetchUsers } from "../../../store/slices/userSlice";
import { usePreventWheelChange } from "../../../utils/usePreventWheelChange";

interface Material {
  productId: string;
  quantity: number;
  unit: UnitType;
  remark: string;
  selectedUnitType?: string;
  sourceProduct?: string;
  type?: "material" | "product";
}

interface ProductSlot {
  productId: string;
  quantity: number;
  productName: string;
}

interface MaterialSlot {
  materialId: string;
  quantity: number;
  materialName: string;
}

interface FloorInwardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Reusable custom React-Select styling
const selectStyles: StylesConfig<any, false> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "42px",
    backgroundColor: "#ffffff",
    borderRadius: "0.5rem",
    borderColor: state.isFocused ? "#0077C0" : "#E2E8F0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 192, 0.15)" : "none",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: state.isFocused ? "#0077C0" : "#CBD5E1",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 12px",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
    color: "#1E293B",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "0.5rem",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #F1F5F9",
    overflow: "hidden",
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "0.875rem",
    backgroundColor: state.isSelected
      ? "#0077C0"
      : state.isFocused
        ? "#F1F5F9"
        : "transparent",
    color: state.isSelected ? "#FFFFFF" : "#334155",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#0077C0",
      color: "#FFFFFF",
    },
  }),
};

const FloorInwardModal: React.FC<FloorInwardModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const handleWheelPrevent = usePreventWheelChange();
  const { rawMaterials } = useSelector((state: RootState) => state.storeInward);
  const users = useSelector((state: RootState) => state.users.user);
  const role = useSelector((state: RootState) => state?.auth.role);
  const { products } = useSelector((state: RootState) => state.product);
  const { loading } = useSelector((state: RootState) => state.floorInward);

  const [requests, setRequests] = useState<Material[]>([]);
  const [activeTab, setActiveTab] = useState<"product" | "material" | "manual">(
    "manual",
  );
  const [materialSearch, setMaterialSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [assignee, setAssignee] = useState("");
  const [productSlots, setProductSlots] = useState<ProductSlot[]>([
    { productId: "", quantity: 1, productName: "" },
  ]);
  const [selectedProductQty, setSelectedProductQty] = useState<number>(1);
  const [materialSlots, setMaterialSlots] = useState<MaterialSlot[]>([
    { materialId: "", quantity: 1, materialName: "" },
  ]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Track parent source dynamic IDs
  const [sourceProductId, setSourceProductId] = useState<string | undefined>(
    undefined,
  );
  const [sourceRawMaterialId, setSourceRawMaterialId] = useState<
    string | undefined
  >(undefined);

  // Initial Fetch Data
  useEffect(() => {
    dispatch(fetchDropdownData("factory"));
  }, [dispatch]);

  // Prevent background body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (users.length <= 0) {
      dispatch(fetchUsers());
    }
  }, [users, dispatch]);

  useEffect(() => {
    if (isOpen && products.length === 0) {
      dispatch(fetchProductsList());
    }
  }, [isOpen, dispatch, products.length]);

  const addMaterial = () => {
    setRequests((prev) => [
      ...prev,
      {
        productId: "",
        quantity: 0,
        unit: "" as UnitType,
        remark: "",
        type: "material",
      },
    ]);
  };

  const updateMaterial = (
    index: number,
    field: keyof Material,
    value: string | number,
  ) => {
    setRequests((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleMaterialSelect = (index: number, selectedOption: any) => {
    const material = rawMaterials.find((m) => m._id === selectedOption?.value);
    if (!material) return;

    const unitType = material.unitType;
    const defaultUnit = getDefaultUnitForType(unitType);

    setRequests((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        productId: selectedOption?.value || "",
        selectedUnitType: unitType,
        unit: defaultUnit as UnitType,
      };
      return updated;
    });
  };

  const updateMaterialSlot = (
    slotIndex: number,
    field: keyof MaterialSlot,
    value: string | number,
  ) => {
    setMaterialSlots((prev) => {
      const updated = [...prev];
      if (field === "materialId") {
        const selectedMaterial = rawMaterials.find(
          (rm: any) => rm._id === value,
        );
        updated[slotIndex] = {
          ...updated[slotIndex],
          [field]: value as string,
          materialName: selectedMaterial?.name || "",
        };
      } else {
        updated[slotIndex] = {
          ...updated[slotIndex],
          [field]: value as number,
        };
      }
      return updated;
    });
  };

  const handleMaterialSlotSelect = (slotIndex: number, selectedOption: any) => {
    updateMaterialSlot(slotIndex, "materialId", selectedOption?.value || "");
  };

  const handleAddMaterialsRawMaterials = async (slotIndex: number) => {
    const slot = materialSlots[slotIndex];
    if (!slot.materialId || slot.quantity <= 0) {
      toast.error("Please select a material product and enter quantity > 0");
      return;
    }
    try {
      const rawMats: any[] = await dispatch(
        getMaterialsRawMaterials(slot.materialId),
      ).unwrap();
      const newRequests: Material[] = rawMats.map((rm) => {
        const unitType = rm.unitType;
        const defaultUnit = getDefaultUnitForType(unitType);
        const multipliedQty = rm.quantity.value * slot.quantity;
        return {
          productId: rm.id,
          quantity: multipliedQty,
          type: rm.type,
          unit: defaultUnit as UnitType,
          selectedUnitType: unitType,
          remark: `For ${slot.quantity} units of ${slot.materialName}`,
          sourceProduct: slot.materialName,
        };
      });

      // Save rawMaterialId and reset productId
      setSourceRawMaterialId(slot.materialId);
      setSourceProductId(undefined);

      setRequests((prev) => [...prev, ...newRequests]);
      setSelectedProductQty(slot.quantity);
      // setMaterialSlots((prev) => prev.filter((_, i) => i !== slotIndex));
      setActiveTab("manual");

      toast.success(
        `${newRequests.length} raw materials added for ${slot.materialName}`,
      );
    } catch (err) {
      toast.error("Failed to fetch product raw materials");
    }
  };

  // const addMaterialSlot = () => {
  //   setMaterialSlots((prev) => [
  //     ...prev,
  //     { materialId: "", quantity: 1, materialName: "" },
  //   ]);
  // };

  const updateProductSlot = (
    slotIndex: number,
    field: keyof ProductSlot,
    value: string | number,
  ) => {
    setProductSlots((prev) => {
      const updated = [...prev];
      if (field === "productId") {
        const selectedProduct = products.find((p: any) => p._id === value);
        updated[slotIndex] = {
          ...updated[slotIndex],
          [field]: value as string,
          productName: selectedProduct?.productName || "",
        };
      } else {
        updated[slotIndex] = {
          ...updated[slotIndex],
          [field]: value as number,
        };
      }
      return updated;
    });
  };

  const handleProductSlotSelect = (slotIndex: number, selectedOption: any) => {
    updateProductSlot(slotIndex, "productId", selectedOption?.value || "");
  };

  const handleAddProductRawMaterials = async (slotIndex: number) => {
    const slot = productSlots[slotIndex];
    if (!slot.productId || slot.quantity <= 0) {
      toast.error("Please select a material product and enter quantity > 0");
      return;
    }
    try {
      const rawMats: any[] = await dispatch(
        getProductRawMaterials(slot.productId),
      ).unwrap();
      const newRequests: Material[] = rawMats.map((rm) => {
        const unitType = rm.unitType;
        const defaultUnit = getDefaultUnitForType(unitType);
        const multipliedQty = rm.quantity.value * slot.quantity;
        return {
          productId: rm.id,
          quantity: multipliedQty,
          type: rm.type,
          unit: defaultUnit as UnitType,
          selectedUnitType: unitType,
          remark: `For ${slot.quantity} units of ${slot.productName}`,
          sourceProduct: slot.productName,
        };
      });

      // Save productId and reset rawMaterialId
      setSourceProductId(slot.productId);
      setSourceRawMaterialId(undefined);

      setRequests((prev) => [...prev, ...newRequests]);
      setSelectedProductQty(slot.quantity);
      // setProductSlots((prev) => prev.filter((_, i) => i !== slotIndex));
      setActiveTab("manual");

      toast.success(
        `${newRequests.length} raw materials added for ${slot.productName}`,
      );
    } catch (err) {
      toast.error("Failed to fetch product raw materials");
    }
  };

  // const addProductSlot = () => {
  //   setProductSlots((prev) => [
  //     ...prev,
  //     { productId: "", quantity: 1, productName: "" },
  //   ]);
  // };

  const handleClose = () => {
    setRequests([]);
    setProductSlots([{ productId: "", quantity: 1, productName: "" }]);
    setMaterialSlots([{ materialId: "", quantity: 1, materialName: "" }]);
    setSourceProductId(undefined);
    setSourceRawMaterialId(undefined);
    setActiveTab("manual");
    setSelectedProductQty(1);
    onClose();
  };

  const removeMaterial = (index: number) => {
    setRequests((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) {
        setSourceProductId(undefined);
        setSourceRawMaterialId(undefined);
      }
      return updated;
    });
  };

  const submitRequests = async () => {
    if (
      requests.some((req) => !req.productId || req.quantity <= 0 || !req.unit)
    ) {
      toast.error(
        "Please fill all material fields (raw material, quantity, unit)",
      );
      return;
    }
    try {
      await dispatch(
        createFloorInwardRequest({
          assignee,
          productId: sourceProductId,
          quantity: selectedProductQty,
          rawMaterialId: sourceRawMaterialId,
          items: requests.map((req) => ({
            productId: req.productId,
            quantity: req.quantity,
            unit: req.unit,
            type: req.type || "material",
            remark: req.remark,
          })),
        }),
      ).unwrap();

      handleClose();
      toast.success("Floor Inward Request created successfully");
    } catch (err) {
      toast.error((err as string) || "Failed to submit requests");
    }
  };

  const fetchRawMaterials = useCallback(
    async (search: string) => {
      try {
        const result = await dispatch(
          fetchRawMaterialsThunk({
            page: 1,
            limit: 20,
            search: search,
            bom: true,
            sortBy: "name",
            sortOrder: "asc",
            from: "factory",
          }),
        ).unwrap();

        return result.data.map((p) => ({
          value: p._id,
          label: p.name,
          unit: p.unit,
        }));
      } catch (err) {
        return [];
      }
    },
    [dispatch],
  );

  const filteredMaterials = useMemo(() => {
    const term = materialSearch.toLowerCase();
    return rawMaterials.filter((mat: IWarehouseRawMaterial) => {
      return (
        mat.name.toLowerCase().includes(term) ||
        mat.sku.toLowerCase().includes(term)
      );
    });
  }, [rawMaterials, materialSearch]);

  const filteredProducts = useMemo(() => {
    const term = productSearch.toLowerCase();
    return products.filter((p: any) =>
      p.productName.toLowerCase().includes(term),
    );
  }, [products, productSearch]);

  const materialSelectOptions = useMemo(() => {
    return filteredMaterials.map((material) => ({
      value: material._id,
      label: `${material.name} (${material.unitType}) - SKU: ${material.sku}`,
    }));
  }, [filteredMaterials]);

  const productSelectOptions = useMemo(() => {
    return filteredProducts.map((p: any) => ({
      value: p._id,
      label: p.productName,
    }));
  }, [filteredProducts]);

  const filteredRequests = requests.filter((req) => {
    if (!searchTerm.trim()) return true;

    const query = searchTerm.toLowerCase();

    // Find the label for the selected material
    const materialOption = materialSelectOptions.find(
      (m) => m.value === req.productId,
    );
    const materialName = materialOption
      ? materialOption.label.toLowerCase()
      : "";

    // Search through fields (Material Name, Source Product, Remark, Unit)
    const matchesMaterial = materialName.includes(query);
    const matchesSource =
      req.sourceProduct?.toLowerCase().includes(query) ?? false;
    const matchesRemark = req.remark.toLowerCase().includes(query);
    const matchesUnit = req.unit.toLowerCase().includes(query);

    return matchesMaterial || matchesSource || matchesRemark || matchesUnit;
  });

  const handleQuantityChange = (quantity: number) => {
    requests.forEach((req) => {
      const currentQty = req.quantity;
      const baseQty = currentQty / selectedProductQty;
      req.quantity = baseQty * quantity;
    });
  };
  const sourceName =
    productSlots[0].productName || materialSlots[0].materialName;
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-sm overflow-hidden animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] sm:max-h-[90vh] flex flex-col border border-slate-100 overflow-hidden transition-all duration-200">
        <div className="shrink-0 border-b border-slate-100 bg-slate-50/80">
          <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2.5 bg-[#0077C0]/10 text-[#0077C0] rounded-xl shrink-0">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 truncate">
                  Add Floor Inward Request
                </h2>
                <p className="text-xs text-slate-500 hidden sm:block">
                  Specify raw materials or batch load from registered products
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-200/60 active:bg-slate-200 transition-all focus:outline-none"
              aria-label="Close modal"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {(role === "admin" || role === "factory" || role === "store") && (
            <div className="px-4 sm:px-6 py-2.5 border-t border-slate-100 bg-white">
              <UserSelectorSearch
                users={users}
                label="Assignee"
                value={assignee}
                onChange={(val) => setAssignee(val as string)}
              />
            </div>
          )}

          <div className="px-4 sm:px-6 bg-white border-t border-slate-100 overflow-x-auto no-scrollbar">
            <div className="flex space-x-2 sm:space-x-6 py-2 sm:py-0">
              {[
                { key: "manual" as const, label: "Raw Materials" },
                ...(sourceName
                  ? []
                  : [
                      { key: "product" as const, label: "BOM Product" },
                      { key: "material" as const, label: "BOM Material" },
                    ]),
              ].map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`py-2 sm:py-3 px-3 sm:px-1 text-xs sm:text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${
                      isActive
                        ? "border-[#0077C0] text-[#0077C0]"
                        : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {tab.label}
                    {tab.key === "manual" && requests.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-[#0077C0] rounded-full font-bold">
                        {requests.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-slate-50/40">
          {activeTab === "manual" && (
            <>
              {requests.length === 0 ? (
                <div className="text-center py-10 px-4 border-2 border-dashed border-slate-200 rounded-2xl bg-white shadow-sm">
                  <div className="w-12 h-12 mx-auto mb-3 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-slate-700 mb-1">
                    No materials in request list
                  </p>
                  <p className="text-xs text-slate-500 mb-4 max-w-sm mx-auto">
                    Add materials manually or generate them using the "By
                    Product" or "By Material" tabs.
                  </p>
                  <button
                    onClick={addMaterial}
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-semibold text-white bg-[#0077C0] hover:bg-[#0064a3] rounded-lg shadow-sm transition-all"
                  >
                    + Add Material
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search requested materials by name, source, or remark..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full h-[42px] pl-10 pr-4 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077C0]/20 focus:border-[#0077C0] transition-all placeholder:text-slate-400 bg-white"
                    />
                    <svg
                      className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {sourceName && (
                    <p className="flex items-center gap-2">
                      <span className="font-medium text-slate-700">
                        {sourceName}:
                      </span>

                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={selectedProductQty}
                        onChange={(e) => {
                          const newQty = e.target.value
                            ? parseFloat(e.target.value)
                            : 0;
                          setSelectedProductQty(newQty);
                          handleQuantityChange(newQty);
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-24 h-8 px-2 border border-slate-200 rounded text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077C0]/20 focus:border-[#0077C0] transition-all"
                      />
                    </p>
                  )}
                  {filteredRequests.length > 0 ? (
                    filteredRequests.map((req) => {
                      // Find actual index in original requests state for updates/deletions
                      const originalIndex = requests.findIndex(
                        (r) => r === req,
                      );

                      return (
                        <div
                          key={originalIndex}
                          className="bg-white p-4 sm:p-5 rounded-xl border border-slate-200/80 shadow-sm space-y-4 relative transition-all hover:border-slate-300"
                        >
                          {req.sourceProduct && (
                            <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 text-[#0077C0] text-xs font-medium border border-blue-100">
                              Source: {req.sourceProduct}
                            </div>
                          )}

                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                              Raw Material{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <Select
                              menuPortalTarget={document.body}
                              options={materialSelectOptions}
                              value={
                                req.productId
                                  ? materialSelectOptions.find(
                                      (m) => m.value === req.productId,
                                    ) || null
                                  : null
                              }
                              onChange={(selectedOption) =>
                                handleMaterialSelect(
                                  originalIndex,
                                  selectedOption,
                                )
                              }
                              placeholder="Search & select raw material..."
                              isSearchable
                              isClearable
                              styles={selectStyles}
                              onInputChange={(value) =>
                                setMaterialSearch(value)
                              }
                            />
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                                Quantity <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="number"
                                placeholder="Enter quantity"
                                onWheel={handleWheelPrevent}
                                value={req.quantity || ""}
                                onChange={(e) =>
                                  updateMaterial(
                                    originalIndex,
                                    "quantity",
                                    e.target.value
                                      ? parseFloat(e.target.value)
                                      : 0,
                                  )
                                }
                                className="w-full h-[42px] px-3.5 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077C0]/20 focus:border-[#0077C0] transition-all placeholder:text-slate-400"
                                min="0"
                                step="any"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                                Unit <span className="text-red-500">*</span>
                              </label>
                              <Select<{ value: UnitType; label: string }>
                                menuPortalTarget={document.body}
                                options={
                                  req.selectedUnitType
                                    ? getUnitsByType(req.selectedUnitType)
                                    : UNITS
                                }
                                value={
                                  req.unit
                                    ? {
                                        value: req.unit,
                                        label:
                                          UNITS.find(
                                            (u) => u.value === req.unit,
                                          )?.label || req.unit,
                                      }
                                    : null
                                }
                                onChange={(selectedOption) =>
                                  updateMaterial(
                                    originalIndex,
                                    "unit",
                                    selectedOption
                                      ? selectedOption.value
                                      : ("" as UnitType),
                                  )
                                }
                                placeholder="Select unit..."
                                isSearchable
                                isClearable
                                styles={selectStyles}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                              Remark
                            </label>
                            <input
                              type="text"
                              placeholder="Add notes or specific instructions..."
                              value={req.remark}
                              onChange={(e) =>
                                updateMaterial(
                                  originalIndex,
                                  "remark",
                                  e.target.value,
                                )
                              }
                              className="w-full h-[42px] px-3.5 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077C0]/20 focus:border-[#0077C0] transition-all placeholder:text-slate-400"
                            />
                          </div>

                          <div className="flex justify-end pt-2 border-t border-slate-100">
                            <button
                              onClick={() => removeMaterial(originalIndex)}
                              className="inline-flex items-center text-red-600 hover:text-red-700 text-xs font-semibold uppercase tracking-wider transition-colors p-1 rounded hover:bg-red-50"
                            >
                              <svg
                                className="w-4 h-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              Remove Line Item
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl">
                      No matching materials found.
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={addMaterial}
                className="w-full py-3 px-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-600 hover:border-[#0077C0] hover:text-[#0077C0] hover:bg-blue-50/40 transition-all flex items-center justify-center gap-2 font-medium text-xs sm:text-sm bg-white"
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Another Material
              </button>
            </>
          )}

          {activeTab === "product" && (
            <>
              {productSlots.map((slot, slotIndex) => (
                <div
                  key={slotIndex}
                  className="bg-white p-4 sm:p-5 rounded-xl border border-slate-200/80 shadow-sm space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                        Select Product <span className="text-red-500">*</span>
                      </label>
                      <Select
                        menuPortalTarget={document.body}
                        options={productSelectOptions}
                        value={
                          slot.productId
                            ? {
                                value: slot.productId,
                                label: slot.productName,
                              }
                            : null
                        }
                        onChange={(selectedOption) =>
                          handleProductSlotSelect(slotIndex, selectedOption)
                        }
                        placeholder="Search and select product..."
                        isSearchable
                        isClearable
                        styles={selectStyles}
                        onInputChange={(value) => setProductSearch(value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                        Product Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        placeholder="1"
                        value={slot.quantity}
                        onChange={(e) =>
                          updateProductSlot(
                            slotIndex,
                            "quantity",
                            e.target.value ? parseFloat(e.target.value) : 1,
                          )
                        }
                        className="w-full h-[42px] px-3.5 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077C0]/20 focus:border-[#0077C0] transition-all"
                        min="1"
                        step="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-2 border-t border-slate-100">
                    <button
                      onClick={() => handleAddProductRawMaterials(slotIndex)}
                      disabled={!slot.productId || slot.quantity <= 0}
                      className="w-full sm:w-auto px-4 py-2.5 bg-[#0077C0] text-white rounded-lg hover:bg-[#0064a3] active:bg-[#005286] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-xs font-semibold tracking-wider uppercase shadow-sm"
                    >
                      Add Raw Materials for Product
                    </button>
                  </div>
                </div>
              ))}

              {/* <button
                onClick={addProductSlot}
                className="w-full py-3 px-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-600 hover:border-[#0077C0] hover:text-[#0077C0] hover:bg-blue-50/40 transition-all flex items-center justify-center gap-2 font-medium text-xs sm:text-sm bg-white"
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Another Product Slot
              </button> */}
            </>
          )}

          {activeTab === "material" && (
            <>
              {materialSlots.map((slot, slotIndex) => (
                <div
                  key={slotIndex}
                  className="bg-white p-4 sm:p-5 rounded-xl border border-slate-200/80 shadow-sm space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                        Select Material <span className="text-red-500">*</span>
                      </label>
                      <AsyncSelect
                        menuPortalTarget={document.body}
                        cacheOptions
                        defaultOptions
                        loadOptions={fetchRawMaterials}
                        value={
                          slot.materialId
                            ? {
                                value: slot.materialId,
                                label: slot.materialName,
                              }
                            : null
                        }
                        onChange={(selectedOption) =>
                          handleMaterialSlotSelect(slotIndex, selectedOption)
                        }
                        placeholder="Type to search material..."
                        isClearable
                        styles={selectStyles}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                        Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        placeholder="1"
                        value={slot.quantity}
                        onChange={(e) =>
                          updateMaterialSlot(
                            slotIndex,
                            "quantity",
                            e.target.value ? parseFloat(e.target.value) : 1,
                          )
                        }
                        className="w-full h-[42px] px-3.5 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0077C0]/20 focus:border-[#0077C0] transition-all"
                        min="1"
                        step="1"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-2 border-t border-slate-100">
                    <button
                      onClick={() => handleAddMaterialsRawMaterials(slotIndex)}
                      disabled={!slot.materialId || slot.quantity <= 0}
                      className="w-full sm:w-auto px-4 py-2.5 bg-[#0077C0] text-white rounded-lg hover:bg-[#0064a3] active:bg-[#005286] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-xs font-semibold tracking-wider uppercase shadow-sm"
                    >
                      Add Raw Materials for Material
                    </button>
                  </div>
                </div>
              ))}

              {/* <button
                onClick={addMaterialSlot}
                className="w-full py-3 px-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-600 hover:border-[#0077C0] hover:text-[#0077C0] hover:bg-blue-50/40 transition-all flex items-center justify-center gap-2 font-medium text-xs sm:text-sm bg-white"
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Another Material Slot
              </button> */}
            </>
          )}
        </div>

        <div className="shrink-0 px-4 sm:px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-slate-500 text-center sm:text-left w-full sm:w-auto">
            {requests.length > 0 ? (
              <span>
                Total items ready: <strong>{requests.length}</strong>
              </span>
            ) : (
              <span>Add items above to continue</span>
            )}
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 sm:flex-initial px-4 py-2.5 border border-slate-300 text-slate-700 hover:bg-slate-100 active:bg-slate-200 rounded-xl font-medium text-xs sm:text-sm transition-all focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitRequests}
              disabled={loading || requests.length === 0}
              className="flex-1 sm:flex-initial px-5 py-2.5 bg-[#0077C0] text-white hover:bg-[#0064a3] active:bg-[#005286] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-semibold text-xs sm:text-sm shadow-md transition-all flex items-center justify-center gap-2 focus:outline-none"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Submitting...</span>
                </>
              ) : (
                <span>Submit Inward Request</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorInwardModal;
